import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

import { Layout } from '@components/Layout';
import { Card } from '@components/Card';
import { Button } from '@components/Button';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { useQuery } from '@tanstack/react-query';
import { getProduct } from '@lib/firebase/products';
import { useCartStore } from '@lib/store/cartStore';
import { formatCurrency } from '@lib/utils';
import { ProductCategory } from '@shared/types/product';
import { ROUTES } from '@lib/routes';

const categoryLabels: Record<ProductCategory, string> = {
  [ProductCategory.BIKE]: 'Bike',
  [ProductCategory.ACCESSORY]: 'Accessory',
  [ProductCategory.PART]: 'Part',
  [ProductCategory.CLOTHING]: 'Clothing',
  [ProductCategory.SAFETY]: 'Safety Gear',
};

export function ProductDetailPage(): JSX.Element {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const addToCart = useCartStore((state) => state.addItem);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => (productId ? getProduct(productId) : null),
    enabled: !!productId,
  });

  const handleAddToCart = (): void => {
    if (!product) return;

    for (let i = 0; i < quantity; i++) {
      addToCart({
        productId: product.id,
        name: product.name,
        price: product.price,
        imageUrl: product.imageUrls[0],
      });
    }

    // Navigate to cart after adding (cart is public, checkout requires auth)
    navigate(ROUTES.CUSTOMER.CART);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-4">Product not found</p>
              <Link to={ROUTES.CUSTOMER.PRODUCTS}>
                <Button>Back to Store</Button>
              </Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Product Images */}
          <div>
            {product.imageUrls && product.imageUrls.length > 0 ? (
              <>
                <div className="mb-4">
                  <img
                    src={product.imageUrls[selectedImageIndex]}
                    alt={product.name}
                    className="w-full h-96 object-cover rounded-lg"
                  />
                </div>
                {product.imageUrls.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {product.imageUrls.map((url, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                          selectedImageIndex === index
                            ? 'border-blue-500'
                            : 'border-gray-200 dark:border-gray-700'
                        }`}
                      >
                        <img
                          src={url}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-96 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <span className="text-gray-400 dark:text-gray-500">No image available</span>
              </div>
            )}
          </div>

          {/* Product Details */}
          <div>
            <div className="mb-4">
              <span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {categoryLabels[product.category]}
              </span>
              {product.isFeatured && (
                <span className="ml-2 px-3 py-1 text-sm rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  Featured
                </span>
              )}
            </div>

            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {product.name}
            </h1>

            <div className="mb-6">
              <div className="flex items-center gap-4 mb-2">
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(product.price)}
                </p>
                {product.compareAtPrice && product.compareAtPrice > product.price && (
                  <>
                    <p className="text-xl text-gray-500 dark:text-gray-400 line-through">
                      {formatCurrency(product.compareAtPrice)}
                    </p>
                    <span className="px-2 py-1 text-sm rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      {Math.round(
                        ((product.compareAtPrice - product.price) / product.compareAtPrice) * 100
                      )}
                      % OFF
                    </span>
                  </>
                )}
              </div>
              {product.sku && (
                <p className="text-sm text-gray-600 dark:text-gray-400">SKU: {product.sku}</p>
              )}
            </div>

            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                {product.description}
              </p>
            </div>

            {/* Specifications */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <Card className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Specifications
                </h2>
                <div className="space-y-2">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}:
                      </span>
                      <span className="text-gray-900 dark:text-white font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Stock Status */}
            <div className="mb-6">
              {product.inStock ? (
                  <p className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium">
                    <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                  In Stock ({product.stock} available)
                </p>
              ) : (
                  <p className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium">
                    <XCircleIcon className="h-4 w-4" aria-hidden="true" />
                  Out of Stock
                </p>
              )}
            </div>

            {/* Add to Cart */}
            {product.inStock && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Quantity:
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={product.stock}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value, 10) || 1)))}
                    className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <Button fullWidth size="lg" onClick={handleAddToCart}>
                  Add to Cart ({quantity})
                </Button>
                <Link to={ROUTES.CUSTOMER.CART}>
                  <Button variant="outline" fullWidth>
                    View Cart
                  </Button>
                </Link>
              </div>
            )}

            {!product.inStock && (
              <Button fullWidth size="lg" disabled>
                Out of Stock
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

